import { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  TextInput,
  Pressable,
  ActivityIndicator,
  Alert,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter, useLocalSearchParams } from "expo-router";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import { useThemeColors } from "@/providers/ThemeProvider";
import { Team } from "@/types";

/**
 * Predefined team colors
 */
const TEAM_COLORS = [
  { name: "Rood", hex: "#EF4444" },
  { name: "Oranje", hex: "#F97316" },
  { name: "Amber", hex: "#F59E0B" },
  { name: "Geel", hex: "#EAB308" },
  { name: "Groen", hex: "#22C55E" },
  { name: "Teal", hex: "#14B8A6" },
  { name: "Blauw", hex: "#3B82F6" },
  { name: "Indigo", hex: "#6366F1" },
  { name: "Paars", hex: "#8B5CF6" },
  { name: "Roze", hex: "#EC4899" },
  { name: "Grijs", hex: "#6B7280" },
  { name: "Zwart", hex: "#1F2937" },
];

/**
 * Edit Team Screen
 */
export default function EditTeamScreen() {
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();
  const { activeWeekend } = useAuth();
  const colors = useThemeColors();

  const [team, setTeam] = useState<Team | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedColor, setSelectedColor] = useState(TEAM_COLORS[0].hex);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  /**
   * Fetch team data
   */
  const fetchTeam = useCallback(async () => {
    if (!id) return;

    try {
      const { data, error } = await supabase
        .from("teams")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;

      setTeam(data);
      setName(data.name);
      setDescription(data.description || "");
      setSelectedColor(data.color || TEAM_COLORS[0].hex);
    } catch (error) {
      console.error("Error fetching team:", error);
      Alert.alert("Fout", "Kon team niet laden", [
        { text: "OK", onPress: () => router.back() },
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [id, router]);

  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  /**
   * Handle save changes
   */
  const handleSave = async () => {
    if (!team) return;

    const trimmedName = name.trim();
    if (!trimmedName) {
      Alert.alert("Fout", "Vul een teamnaam in");
      return;
    }

    setIsSaving(true);

    try {
      const { error } = await supabase
        .from("teams")
        .update({
          name: trimmedName,
          description: description.trim() || null,
          color: selectedColor,
        })
        .eq("id", team.id);

      if (error) throw error;

      Alert.alert("Opgeslagen! ✓", "Team is bijgewerkt", [
        { text: "OK", onPress: () => router.back() },
      ]);
    } catch (error: any) {
      console.error("Error saving team:", error);
      Alert.alert("Fout", error.message || "Kon team niet opslaan");
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Handle delete team
   */
  const handleDelete = () => {
    if (!team) return;

    Alert.alert(
      "Team Verwijderen",
      `Weet je zeker dat je "${team.name}" wilt verwijderen?\n\nAlle teamleden worden losgekoppeld.`,
      [
        { text: "Annuleer", style: "cancel" },
        {
          text: "Verwijderen",
          style: "destructive",
          onPress: async () => {
            setIsDeleting(true);

            try {
              const { error } = await supabase
                .from("teams")
                .delete()
                .eq("id", team.id);

              if (error) throw error;

              Alert.alert("Verwijderd", "Team is verwijderd", [
                { text: "OK", onPress: () => router.back() },
              ]);
            } catch (error: any) {
              console.error("Error deleting team:", error);
              Alert.alert("Fout", error.message || "Kon team niet verwijderen");
            } finally {
              setIsDeleting(false);
            }
          },
        },
      ]
    );
  };

  if (isLoading) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
        edges={["bottom"]}
      >
        <ActivityIndicator size="large" color="#D97706" />
        <Text className="mt-2" style={{ color: "#D97706" }}>
          Team laden...
        </Text>
      </SafeAreaView>
    );
  }

  if (!team) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
        edges={["bottom"]}
      >
        <Ionicons name="alert-circle-outline" size={48} color="#D97706" />
        <Text className="mt-2" style={{ color: "#D97706" }}>
          Team niet gevonden
        </Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView
      className="flex-1"
      style={{ backgroundColor: colors.background }}
      edges={["bottom"]}
    >
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        className="flex-1"
        keyboardVerticalOffset={Platform.OS === "ios" ? 90 : 0}
      >
        <ScrollView
          className="flex-1 px-4"
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          contentContainerStyle={{ paddingBottom: 40 }}
        >
          {/* Preview */}
          <View className="items-center py-6">
            <View
              className="w-24 h-24 rounded-full items-center justify-center mb-3"
              style={{ backgroundColor: `${selectedColor}20` }}
            >
              <Text
                className="text-4xl font-bold"
                style={{ color: selectedColor }}
              >
                {name.trim().charAt(0).toUpperCase() || "T"}
              </Text>
            </View>
            <Text
              className="text-xl font-bold"
              style={{ color: selectedColor }}
            >
              {name.trim() || "Team Naam"}
            </Text>
            {description.trim() && (
              <Text className="italic mt-1" style={{ color: colors.textSecondary }}>
                "{description.trim()}"
              </Text>
            )}
            <View className="flex-row items-center mt-2">
              <Text className="text-2xl font-bold" style={{ color: selectedColor }}>
                {team.total_score}
              </Text>
              <Text className="ml-1" style={{ color: colors.textTertiary }}>punten</Text>
            </View>
          </View>

          {/* Form */}
          <View
            className="rounded-2xl p-4 mb-4"
            style={{
              backgroundColor: colors.surface,
              borderWidth: 1,
              borderColor: colors.border,
            }}
          >
            {/* Team Name */}
            <View className="mb-4">
              <Text className="text-sm font-medium mb-2" style={{ color: colors.text }}>
                Team Naam <Text style={{ color: "#EF4444" }}>*</Text>
              </Text>
              <TextInput
                className="rounded-xl px-4 py-3"
                style={{
                  backgroundColor: colors.background,
                  borderWidth: 1,
                  borderColor: colors.border,
                  color: colors.text,
                }}
                placeholder="bijv. Team Rood"
                placeholderTextColor={colors.textTertiary}
                value={name}
                onChangeText={setName}
                maxLength={30}
                autoCapitalize="words"
              />
              <Text className="text-xs mt-1 text-right" style={{ color: colors.textTertiary }}>
                {name.length}/30
              </Text>
            </View>

            {/* Team Slogan */}
            <View className="mb-4">
              <Text className="text-sm font-medium mb-2" style={{ color: colors.text }}>
                Slogan / Motto (optioneel)
              </Text>
              <TextInput
                className="rounded-xl px-4 py-3"
                style={{
                  backgroundColor: colors.background,
                  borderWidth: 1,
                  borderColor: colors.border,
                  color: colors.text,
                }}
                placeholder="bijv. Wij winnen altijd!"
                placeholderTextColor={colors.textTertiary}
                value={description}
                onChangeText={setDescription}
                maxLength={50}
              />
              <Text className="text-xs mt-1 text-right" style={{ color: colors.textTertiary }}>
                {description.length}/50
              </Text>
            </View>

            {/* Team Color */}
            <View>
              <Text className="text-sm font-medium mb-3" style={{ color: colors.text }}>
                Team Kleur
              </Text>
              <View className="flex-row flex-wrap">
                {TEAM_COLORS.map((color) => (
                  <Pressable
                    key={color.hex}
                    onPress={() => setSelectedColor(color.hex)}
                    className="mr-2 mb-2"
                    accessibilityRole="button"
                    accessibilityLabel={`Selecteer ${color.name}`}
                  >
                    <View
                      className="w-12 h-12 rounded-xl items-center justify-center"
                      style={{
                        backgroundColor: color.hex,
                        borderWidth: selectedColor === color.hex ? 2 : 0,
                        borderColor: colors.text,
                      }}
                    >
                      {selectedColor === color.hex && (
                        <Ionicons name="checkmark" size={24} color="#FFFFFF" />
                      )}
                    </View>
                  </Pressable>
                ))}
              </View>
            </View>
          </View>

          {/* Danger Zone */}
          <View
            className="rounded-2xl p-4"
            style={{
              backgroundColor: "#FEF2F2",
              borderWidth: 1,
              borderColor: "#FECACA",
            }}
          >
            <Text className="text-sm font-semibold mb-2" style={{ color: "#B91C1C" }}>
              Gevarenzone
            </Text>
            <Text className="text-sm mb-3" style={{ color: "#DC2626" }}>
              Het verwijderen van een team kan niet ongedaan worden gemaakt.
            </Text>
            <Pressable
              onPress={handleDelete}
              disabled={isDeleting}
              className="rounded-xl py-3 items-center active:opacity-80"
              style={{ backgroundColor: "#FEE2E2" }}
              accessibilityRole="button"
              accessibilityLabel="Team verwijderen"
            >
              {isDeleting ? (
                <ActivityIndicator color="#EF4444" />
              ) : (
                <View className="flex-row items-center">
                  <Ionicons name="trash-outline" size={18} color="#EF4444" />
                  <Text className="font-medium ml-2" style={{ color: "#DC2626" }}>
                    Team Verwijderen
                  </Text>
                </View>
              )}
            </Pressable>
          </View>
        </ScrollView>

        {/* Submit Button */}
        <View className="px-4 pb-4" style={{ backgroundColor: colors.background }}>
          <Pressable
            onPress={handleSave}
            disabled={isSaving || !name.trim()}
            className="rounded-xl py-4 items-center"
            style={{
              backgroundColor: isSaving || !name.trim() ? colors.border : "#F59E0B",
            }}
            accessibilityRole="button"
            accessibilityLabel="Wijzigingen opslaan"
          >
            {isSaving ? (
              <ActivityIndicator color="#FFFFFF" />
            ) : (
              <Text className="text-white font-semibold text-lg">
                Wijzigingen Opslaan
              </Text>
            )}
          </Pressable>
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}
